import { useTranslation } from 'react-i18next';
import styles from './sign-up-form.module.scss';
import { useMemo } from 'react';
import * as yup from 'yup';
import { Formik, useField } from 'formik';
import {
  Tooltip,
  FormikTextInputField,
  Button,
  TextInputField,
} from '@spotted-zebra-uk/ui-components';
import { getPasswordValidation } from '@/utils/validation';
import PrivacyNotice from '@/components/Elements/PrivacyNotice/PrivacyNotice';

type EmailPasswordFormProps = {
  email?: string;
  givenName?: string;
  familyName?: string;
  submitting: boolean;
  autofillAndLock: boolean;
  handleSignUp: (
    email: string,
    password: string,
    givenName: string,
    familyName: string
  ) => void;
};

export const SignUpForm = ({
  email = '',
  givenName = '',
  familyName = '',
  submitting,
  handleSignUp,
  autofillAndLock = false,
}: EmailPasswordFormProps) => {
  const { t } = useTranslation();

  const initialValues = {
    email: email,
    givenName: givenName,
    familyName: familyName,
    password: '',
  };

  const validationForm = useMemo(() => {
    return yup.object().shape({
      givenName: yup
        .string()
        .required(t('forms.requiredField'))
        .max(50, t('forms.nameIsTooLong')),
      familyName: yup
        .string()
        .required(t('forms.requiredField'))
        .max(50, t('forms.nameIsTooLong')),
      email: yup
        .string()
        .email(t('forms.emailIsInvalid'))
        .required(t('forms.emailIsInvalid')),
      password: getPasswordValidation(t),
    });
  }, []);

  const onSubmit = (values: {
    email: string;
    password: string;
    givenName: string;
    familyName: string;
  }) => {
    handleSignUp(
      values.email,
      values.password,
      values.givenName,
      values.familyName
    );
  };

  return (
    <>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationForm}
      >
        {({ handleSubmit }) => (
          <div>
            <FormikTextInputField
              id="givenName"
              label={t('forms.givenName')}
              placeholder={t('forms.givenName')}
              data-testid="sign-up__given-name"
              useFormikField={useField}
            />
            <FormikTextInputField
              id="familyName"
              label={t('forms.familyName')}
              placeholder={t('forms.familyName')}
              data-testid="sign-up__family-name"
              useFormikField={useField}
            />
            {autofillAndLock ? (
              <Tooltip
                content={t('signUp.emailLocked')}
                disabled={!autofillAndLock}
              >
                <TextInputField
                  id="email"
                  value={email}
                  label={t('forms.email')}
                  placeholder={t('forms.email')}
                  disabled={autofillAndLock}
                  onChange={() => {}}
                  data-testid="sign-up__email"
                />
              </Tooltip>
            ) : (
              <FormikTextInputField
                id="email"
                label={t('forms.email')}
                placeholder={t('forms.email')}
                disabled={autofillAndLock}
                data-testid="sign-up__email"
                useFormikField={useField}
              />
            )}
            <FormikTextInputField
              id="password"
              label={t('forms.password')}
              placeholder={t('forms.password')}
              type="password"
              data-testid="sign-up__password"
              useFormikField={useField}
            />
            <PrivacyNotice />
            <Button
              onClick={() => handleSubmit()}
              fullWidth
              className={styles.submitButton}
              disabled={submitting}
              data-testid="sign-up__create-account-button"
            >
              {t('signUp.createAccount')}
            </Button>
          </div>
        )}
      </Formik>
    </>
  );
};
